/**
 * Offre commerciale (docs/offre-paliers.md) : essai gratuit de 30 jours, seule
 * offre gratuite, puis deux paliers mensuels — Essentiel et Pro — à quotas
 * remis à zéro le 1er du mois. Règles partagées : l'API les applique, le front
 * les affiche — une seule définition, aucun écart possible.
 */
export const TRIAL_DAYS = 30;
export const TRIAL_INVOICE_LIMIT = 10;
/** Usages de l'IA. Pendant l'essai, chacun a son propre plafond (sur toute la durée). */
export const AI_USAGES = ['invoice_draft', 'quote_draft', 'expense_scan'];
export const AI_TRIAL_LIMITS = {
    invoice_draft: 20,
    quote_draft: 20,
    expense_scan: 20,
};
export const AI_USAGE_LABELS = {
    invoice_draft: 'générations de facture',
    quote_draft: 'générations de devis',
    expense_scan: 'analyses de justificatif',
};
/** WhatsApp masqué tant que Meta n'a pas validé le numéro (docs/telephonie.md). */
export const WHATSAPP_AVAILABLE = false;
export const BILLING_INTERVALS = ['month', 'year'];
export const PLAN_IDS = ['essential', 'pro', 'expert'];
export const PLANS = {
    essential: { label: 'Essentiel', priceEur: 9, yearlyPriceEur: 90, invoicesPerMonth: 20, aiDraftsPerMonth: 60, scansPerMonth: 60, onlineDeposits: false, quoteReminders: false, accountantAccess: false, phoneAssistant: false, signaturesPerMonth: 0 },
    pro: { label: 'Pro', priceEur: 24, yearlyPriceEur: 240, invoicesPerMonth: 60, aiDraftsPerMonth: 250, scansPerMonth: 250, onlineDeposits: true, quoteReminders: true, accountantAccess: true, phoneAssistant: false, signaturesPerMonth: 30 },
    // Répondeur : un numéro dédié par abonné (abonnement Twilio + transcription) → Expert seulement.
    expert: { label: 'Expert', priceEur: 39, yearlyPriceEur: 390, invoicesPerMonth: 200, aiDraftsPerMonth: 600, scansPerMonth: 600, onlineDeposits: true, quoteReminders: true, accountantAccess: true, phoneAssistant: true, signaturesPerMonth: 100 },
};
/** Usage IA → quota du palier : factures et devis partagent le même. */
export function planAiLimit(plan, usage) {
    return usage === 'expense_scan' ? PLANS[plan].scansPerMonth : PLANS[plan].aiDraftsPerMonth;
}
/** Usages comptés ensemble dans un palier (factures et devis rédigés par l'IA). */
export function planAiUsagesSharing(usage) {
    return usage === 'expense_scan' ? ['expense_scan'] : ['invoice_draft', 'quote_draft'];
}
/**
 * - `trial`    essai gratuit, borné en durée et en nombre de factures ;
 * - `active`   abonné à un palier (Stripe, ou activation manuelle par un admin) ;
 * - `canceled` abonnement arrêté : lecture seule, plus d'envoi.
 */
export const SUBSCRIPTION_STATUSES = ['trial', 'active', 'canceled'];
const DAY_MS = 86_400_000;
export function entitlement(input) {
    const now = input.now ?? new Date();
    const base = { status: input.status, plan: input.plan, invoicesSent: input.invoicesSent };
    // Les administrateurs exploitent la plateforme : jamais limités.
    if (input.isAdmin) {
        return { ...base, invoiceLimit: null, daysLeft: null, canSend: true, blockedReason: null };
    }
    if (input.status === 'canceled') {
        return {
            ...base,
            invoiceLimit: 0,
            daysLeft: null,
            canSend: false,
            blockedReason: "Votre abonnement est arrêté : l'envoi de factures est suspendu. Vos factures restent consultables.",
        };
    }
    if (input.status === 'active') {
        // Un abonné sans palier connu (donnée incohérente) est traité comme Essentiel.
        const plan = PLANS[input.plan ?? 'essential'];
        const limit = plan.invoicesPerMonth;
        if (input.invoicesSent >= limit) {
            return {
                ...base,
                invoiceLimit: limit,
                daysLeft: null,
                canSend: false,
                blockedReason: input.plan === 'expert'
                    ? `Vous avez émis les ${limit} factures de votre offre Expert ce mois-ci. Contactez-nous pour un volume supérieur ; le quota repart le 1er du mois.`
                    : input.plan === 'pro'
                        ? `Vous avez émis les ${limit} factures de votre offre Pro ce mois-ci : passez à l'offre Expert (${PLANS.expert.invoicesPerMonth} factures) ou attendez le 1er du mois.`
                        : `Vous avez émis les ${limit} factures de votre offre ${plan.label} ce mois-ci : passez à l'offre Pro (${PLANS.pro.invoicesPerMonth} factures) ou attendez le 1er du mois.`,
            };
        }
        return { ...base, invoiceLimit: limit, daysLeft: null, canSend: true, blockedReason: null };
    }
    const remainingMs = input.trialEndsAt ? input.trialEndsAt.getTime() - now.getTime() : 0;
    const daysLeft = Math.max(0, Math.ceil(remainingMs / DAY_MS));
    if (remainingMs <= 0) {
        return {
            ...base,
            invoiceLimit: TRIAL_INVOICE_LIMIT,
            daysLeft: 0,
            canSend: false,
            blockedReason: `Votre essai gratuit de ${TRIAL_DAYS} jours est terminé : choisissez l'offre Essentiel, Pro ou Expert pour continuer à émettre des factures.`,
        };
    }
    if (input.invoicesSent >= TRIAL_INVOICE_LIMIT) {
        return {
            ...base,
            invoiceLimit: TRIAL_INVOICE_LIMIT,
            daysLeft,
            canSend: false,
            blockedReason: `Vous avez émis les ${TRIAL_INVOICE_LIMIT} factures de l'essai gratuit : choisissez l'offre Essentiel, Pro ou Expert pour continuer.`,
        };
    }
    return { ...base, invoiceLimit: TRIAL_INVOICE_LIMIT, daysLeft, canSend: true, blockedReason: null };
}
/** Fin d'essai pour un compte créé à `createdAt`. */
export function trialEndFrom(createdAt) {
    return new Date(createdAt.getTime() + TRIAL_DAYS * DAY_MS);
}
/** Premier jour du mois de `date`, à 00:00 heure locale : début des quotas mensuels. */
export function monthStart(date = new Date()) {
    return new Date(date.getFullYear(), date.getMonth(), 1);
}
/** Acompte en ligne : offre Pro, essai (tout est inclus) et administrateurs. */
/** Relances automatiques de devis et d'acomptes : essai, Pro, administrateurs. */
export function canAutoRemindQuotes(subscription, isAdmin) {
    if (isAdmin || subscription.status === 'trial')
        return true;
    return subscription.status === 'active' && PLANS[subscription.plan ?? 'essential'].quoteReminders;
}
export function canTakeOnlineDeposits(subscription, isAdmin) {
    if (isAdmin)
        return true;
    if (subscription.status === 'trial')
        return true;
    return subscription.status === 'active' && PLANS[subscription.plan ?? 'essential'].onlineDeposits;
}
/** Accès expert-comptable : essai (tout est inclus), Pro, administrateurs. */
export function canInviteAccountant(subscription, isAdmin) {
    if (isAdmin || subscription.status === 'trial')
        return true;
    return subscription.status === 'active' && PLANS[subscription.plan ?? 'essential'].accountantAccess;
}
/** Répondeur et demandes WhatsApp : Expert et administrateurs (un numéro payant par abonné : pas dans l'essai). */
export function canUsePhoneAssistant(subscription, isAdmin) {
    if (isAdmin)
        return true;
    return subscription.status === 'active' && PLANS[subscription.plan ?? 'essential'].phoneAssistant;
}
/** Signatures par essai (sur toute sa durée). */
export const TRIAL_SIGNATURE_LIMIT = 5;
/** Envois en signature : `null` illimité (admins), 0 non inclus. Essai : sur toute sa durée. */
export function signatureLimit(subscription, isAdmin) {
    if (isAdmin)
        return null;
    if (subscription.status === 'trial')
        return TRIAL_SIGNATURE_LIMIT;
    if (subscription.status !== 'active')
        return 0;
    return PLANS[subscription.plan ?? 'essential'].signaturesPerMonth;
}
//# sourceMappingURL=plan.js.map